import React, { useContext } from 'react';
import { ColumnSelectOption } from 'lib/components/ColumnSelect';
import { ColumnSelectItem } from 'lib/components/ColumnSelect/ColumnSelectItem';
import { PopoverContext } from 'lib/components/Popover';

type AutocompletePanelBaseProps<T extends string> = {
  selectedOptions: (ColumnSelectOption<T> | undefined)[];
  highlightedIndex: number;
  multiselect?: boolean;
  selectOption: (option: ColumnSelectOption<T>) => void;
};

type AutocompletePanelProps<T extends string> =
  AutocompletePanelBaseProps<T> & {
    filteredOptions: ColumnSelectOption<T>[];
    loading?: boolean;
    noOptionsMessage?: string;
  };

type OptionProps<T extends string> = {
  option: ColumnSelectOption<T>;
  selected: boolean;
  active: boolean;
  setOpen: (open: boolean) => void;
} & Pick<AutocompletePanelBaseProps<T>, 'selectOption' | 'multiselect'>;

function Option<T extends string>({
  option,
  selected,
  active,
  selectOption,
  multiselect
}: OptionProps<T>) {
  return (
    <li
      key={option.value}
      role="option"
      aria-selected={selected}
      onMouseDown={e => {
        multiselect && e.preventDefault();
        e.stopPropagation();
        selectOption(option);
      }}
    >
      <ColumnSelectItem option={option} active={active} selected={selected} />
    </li>
  );
}

export function AutocompletePanel<T extends string>({
  loading,
  filteredOptions,
  selectedOptions,
  highlightedIndex,
  selectOption,
  multiselect,
  noOptionsMessage = 'No options'
}: AutocompletePanelProps<T>) {
  const { setOpen } = useContext(PopoverContext);

  return (
    <ul className="text-sm p-2 max-h-64 overflow-y-scroll">
      {(filteredOptions.length === 0 || loading) && (
        <div className="text-center py-2 text-column-gray-600">
          {loading ? 'Loading...' : noOptionsMessage}
        </div>
      )}

      {!loading &&
        filteredOptions.length > 0 &&
        filteredOptions.map((option, index) => {
          const selected = !!selectedOptions.find(
            optionMatch => optionMatch?.value === option.value
          );
          const active = index === highlightedIndex;
          return (
            <Option
              key={index}
              option={option}
              selectOption={selectOption}
              selected={selected}
              active={active}
              multiselect={multiselect}
              setOpen={setOpen}
            />
          );
        })}
    </ul>
  );
}
